'use client';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type {
  GetSkinAnalysisRecommendationProductDetailRequest,
  GetSkinAnalysisRecommendationProductsRequest,
} from '../../infrastructure/skinAnalysis/utils/types';
import { SKIN_ANALYSIS_QUERIES } from '../../infrastructure/skinAnalysis/utils/queries';
import type { SkinAnalysisResultRef, SkinAnalysisSurveyQuery } from '../../model/skinAnalysis/skinAnalysis';

export const useSkinAnalysisService = () => {
  // ----- me/status -----
  const useSuspenseSkinAnalysisMyStatus = () => useSuspenseQuery(SKIN_ANALYSIS_QUERIES.getMyStatus());

  // ----- me/overview: the Skin Diary entry page, in one call -----
  const useSuspenseSkinAnalysisMyOverview = (userId?: string | number) =>
    useSuspenseQuery(SKIN_ANALYSIS_QUERIES.getMyOverview(userId));

  // ----- survey -----
  /** `currency` renders the budget question's labels; omitting it prices them in USD. */
  const useSuspenseSkinAnalysisSurvey = (params: SkinAnalysisSurveyQuery = {}) =>
    useSuspenseQuery(SKIN_ANALYSIS_QUERIES.getActiveSurvey(params));

  /** Non-suspense, failure-tolerant active-survey read (submit path); 404 leaves `data` undefined. */
  const useSkinAnalysisSurvey = (params: SkinAnalysisSurveyQuery = {}) =>
    useQuery(SKIN_ANALYSIS_QUERIES.getActiveSurveySafe(params));

  const useSubmitSkinAnalysisSurveyResponse = () =>
    useMutation(SKIN_ANALYSIS_QUERIES.submitSurveyResponse(useQueryClient()));

  /**
   * The caller's submitted survey, restored read-only. Distinct from the ACTIVE survey above:
   * that one is a questionnaire to fill in, this one is what they already answered.
   */
  const useSuspenseSkinAnalysisMySurvey = () => useSuspenseQuery(SKIN_ANALYSIS_QUERIES.getMySurvey());

  // ----- location -----
  const useSuspenseSkinAnalysisLocation = () => useSuspenseQuery(SKIN_ANALYSIS_QUERIES.getLocation());

  /** Non-suspense, failure-tolerant location read: only pre-fills the region card. */
  const useSkinAnalysisLocation = () => useQuery(SKIN_ANALYSIS_QUERIES.getLocationSafe());

  const usePutSkinAnalysisLocation = () => useMutation(SKIN_ANALYSIS_QUERIES.putLocation(useQueryClient()));

  /**
   * Warms the saved/inferred location from a screen heading for one that names it.
   *
   * Same shape and reasoning as usePrefetchSkinAnalysisEnvironment below, and the same
   * Safe variant so the warm path and the read path share one failure policy. Worth its
   * own warm because the read is what names the region: a screen that reaches it cold
   * has nothing to show for a full round trip.
   */
  const usePrefetchSkinAnalysisLocation = () => {
    const queryClient = useQueryClient();
    useEffect(() => {
      void queryClient.prefetchQuery(SKIN_ANALYSIS_QUERIES.getLocationSafe());
    }, [queryClient]);
  };

  /** Live climate for the caller's location. Unresolvable climate reads as `undefined`, not an error. */
  const useSkinAnalysisEnvironment = () => useQuery(SKIN_ANALYSIS_QUERIES.getEnvironmentSafe());

  /**
   * Warms the climate read from a screen that knows the user is heading for one that shows it.
   *
   * Prefetch rather than `useSkinAnalysisEnvironment()`, for two reasons: the calling screen
   * does not subscribe, so it never re-renders when the reading lands; and `prefetchQuery`
   * resolves rather than rejects on failure, so a warming screen can never be taken down by
   * a request it does not even display. The consumer's own hook reads the same cache entry.
   */
  const usePrefetchSkinAnalysisEnvironment = () => {
    const queryClient = useQueryClient();
    useEffect(() => {
      // The Safe variant, not a hand-copied `retry: false`: the warm path and the read
      // path must share one failure policy for this cache entry, so the policy has to
      // have one definition. (prefetchQuery never throws, so throwOnError is inert here.)
      void queryClient.prefetchQuery(SKIN_ANALYSIS_QUERIES.getEnvironmentSafe());
    }, [queryClient]);
  };

  /** Worldwide city autocomplete (Open-Meteo, server-side). Debounced by the caller. */
  const useSearchSkinAnalysisLocations = () => useMutation(SKIN_ANALYSIS_QUERIES.searchLocations());

  // ----- capture / results -----
  const useCreateSkinAnalysisImageUploadUrl = () => useMutation(SKIN_ANALYSIS_QUERIES.createImageUploadUrl());

  const useCreateSkinAnalysisHautRun = () => useMutation(SKIN_ANALYSIS_QUERIES.createHautRun());

  const useCreateSkinAnalysisResult = () => useMutation(SKIN_ANALYSIS_QUERIES.createResult(useQueryClient()));

  /** Non-suspense so the UI can render in-progress branches; refetchInterval lives in the query factory. */
  const useSkinAnalysisResultStatusPolling = (params: SkinAnalysisResultRef, enabled = true) => {
    const queryClient = useQueryClient();
    const options = SKIN_ANALYSIS_QUERIES.pollResultStatus(params, queryClient);
    const query = useQuery({ ...options, enabled: enabled && Boolean(params.resultId) });
    useEffect(() => {
      if (enabled) {
        return;
      }
      // Disabling an observer stops its interval, but a request's retry backoff can
      // still be running. Cancel it only when no other screen needs this result.
      void queryClient.cancelQueries({
        queryKey: SKIN_ANALYSIS_QUERIES.pollResultStatus({ resultId: params.resultId }, queryClient).queryKey,
        exact: true,
        predicate: (cached) => !cached.isActive(),
      });
    }, [enabled, params.resultId, queryClient]);
    return query;
  };

  // ----- report / recommendations -----
  const useSuspenseSkinAnalysisReport = (params: SkinAnalysisResultRef) =>
    useSuspenseQuery(SKIN_ANALYSIS_QUERIES.getReport(params));

  const useSuspenseSkinAnalysisRecommendations = (params: SkinAnalysisResultRef) =>
    useSuspenseQuery(SKIN_ANALYSIS_QUERIES.getRecommendations(params));

  // ----- live recommendation products (SSD-6340) -----
  /**
   * Non-suspense on purpose: the routine renders its copy from the snapshot the moment that
   * arrives, and the live prices land beside it. A failure here — a 503 the contract forbids
   * reading as "sold out" — must reach the screen as a state it can explain and retry, not
   * unmount the routine into an error boundary.
   */
  const useSkinAnalysisRecommendationProducts = (params: GetSkinAnalysisRecommendationProductsRequest) =>
    useQuery(SKIN_ANALYSIS_QUERIES.getRecommendationProducts(params));

  /**
   * Non-suspense: the product sheet fetches on open and draws its own loading, gone and
   * unavailable states, so a failure here must reach the sheet rather than an error boundary
   * above it. `enabled` lets the caller hold the read until the sheet is actually open.
   */
  const useSkinAnalysisRecommendationProductDetail = (
    params: GetSkinAnalysisRecommendationProductDetailRequest & { enabled?: boolean },
  ) => {
    const { enabled = true, ...request } = params;
    const options = SKIN_ANALYSIS_QUERIES.getRecommendationProductDetail(request);
    return useQuery({ ...options, enabled: enabled && options.enabled });
  };

  /**
   * Warms the recommendations read while the user is still on the report, so the routine
   * screens downstream open from cache. Same shape as usePrefetchSkinAnalysisEnvironment:
   * the warming screen never subscribes, and `prefetchQuery` resolves rather than rejects
   * on failure. Guarded here because prefetchQuery ignores the query's `enabled` flag.
   */
  const usePrefetchSkinAnalysisRecommendations = (params: SkinAnalysisResultRef & { enabled?: boolean }) => {
    const queryClient = useQueryClient();
    const { resultId, enabled = true } = params;
    useEffect(() => {
      // `enabled` exists for the progressive contract (Facade #1026): recommendations 409
      // until their section completes, and prefetching the conflict would seed the cache
      // with an error the routine screen then trips over.
      if (!resultId || !enabled) {
        return;
      }
      void queryClient.prefetchQuery(SKIN_ANALYSIS_QUERIES.getRecommendations({ resultId }));
    }, [queryClient, resultId, enabled]);
  };

  return {
    useSuspenseSkinAnalysisMyStatus,
    useSuspenseSkinAnalysisMyOverview,
    useSuspenseSkinAnalysisSurvey,
    useSkinAnalysisSurvey,
    useSuspenseSkinAnalysisMySurvey,
    useSubmitSkinAnalysisSurveyResponse,
    useSuspenseSkinAnalysisLocation,
    useSkinAnalysisLocation,
    useSkinAnalysisEnvironment,
    usePrefetchSkinAnalysisEnvironment,
    usePrefetchSkinAnalysisLocation,
    usePutSkinAnalysisLocation,
    useSearchSkinAnalysisLocations,
    useCreateSkinAnalysisImageUploadUrl,
    useCreateSkinAnalysisHautRun,
    useCreateSkinAnalysisResult,
    useSkinAnalysisRecommendationProducts,
    useSkinAnalysisRecommendationProductDetail,
    useSkinAnalysisResultStatusPolling,
    useSuspenseSkinAnalysisReport,
    useSuspenseSkinAnalysisRecommendations,
    usePrefetchSkinAnalysisRecommendations,
  };
};
